import React, { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';
import { useData } from '../context/DataContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, 
  Plus, 
  Trash2, 
  Edit3, 
  X,
  HelpCircle,
  Tag,
  Box,
  CheckCircle2,
  Truck,
  ArrowUp,
  ArrowDown,
  Globe
} from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, setDoc, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';

const AdminProducts = () => {
  const { data } = useData();
  const products = data.products || [];
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [modalLang, setModalLang] = useState('en'); // 'en' | 'mr'
  
  // Capsule Filter State: 'all' | 'Food Products' | 'Industrial & Agricultural Products'
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Form Category switcher state inside editing modal
  const [formCategory, setFormCategory] = useState('Food Products');

  useEffect(() => {
    if (editingProduct) {
      setUploadedImageUrl(editingProduct.image || '');
      // Determine initial form category safely
      const cat = editingProduct.category;
      if (cat === 'Industrial & Agricultural Products' || cat === 'Other') {
        setFormCategory('Industrial & Agricultural Products');
      } else {
        setFormCategory('Food Products');
      }
      document.body.classList.add('admin-modal-open');
    } else {
      document.body.classList.remove('admin-modal-open');
    }
    return () => document.body.classList.remove('admin-modal-open');
  }, [editingProduct]);

  // Save Product to Firestore
  const handleSaveProduct = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const selectedCategory = formData.get('category') || formCategory;
    const isFoodProduct = selectedCategory === 'Food Products';

    const name = (formData.get('name') || '').trim();
    const nameMr = (formData.get('nameMr') || '').trim();
    const desc = (formData.get('desc') || '').trim();
    const descMr = (formData.get('descMr') || '').trim();

    if (!name && !nameMr) {
      Swal.fire({
        title: 'Missing Required Fields',
        text: 'Please enter a product name in English or Marathi.',
        icon: 'warning',
        confirmButtonColor: '#2d6a4f'
      });
      return;
    }

    const featuresRaw = formData.get('features') || '';
    const features = featuresRaw.split(',').map(f => f.trim()).filter(Boolean);
    const featuresMrRaw = formData.get('featuresMr') || '';
    const featuresMr = featuresMrRaw.split(',').map(f => f.trim()).filter(Boolean);

    let productData = {
      name: name || nameMr,
      nameMr: nameMr || name,
      category: selectedCategory,
      desc: desc || descMr,
      descMr: descMr || desc,
      image: uploadedImageUrl,
      features: features.length > 0 ? features : featuresMr,
      featuresMr: featuresMr.length > 0 ? featuresMr : features
    };

    if (isFoodProduct) {
      const packingRaw = formData.get('packingSizes') || '';
      const packingSizes = packingRaw.split(',').map(s => s.trim()).filter(Boolean);
      const packingMrRaw = formData.get('packingSizesMr') || '';
      const packingSizesMr = packingMrRaw.split(',').map(s => s.trim()).filter(Boolean);

      productData.packingSizes = packingSizes.length > 0 ? packingSizes : packingSizesMr;
      productData.packingSizesMr = packingSizesMr.length > 0 ? packingSizesMr : packingSizes;
    } else {
      const benefitsRaw = formData.get('benefits') || '';
      const benefits = benefitsRaw.split(',').map(b => b.trim()).filter(Boolean);
      const benefitsMrRaw = formData.get('benefitsMr') || '';
      const benefitsMr = benefitsMrRaw.split(',').map(b => b.trim()).filter(Boolean);

      const packagingRaw = formData.get('packagingOptions') || '';
      const packagingOptions = packagingRaw.split(',').map(p => p.trim()).filter(Boolean);
      const packagingMrRaw = formData.get('packagingOptionsMr') || '';
      const packagingMr = packagingMrRaw.split(',').map(p => p.trim()).filter(Boolean);

      productData.benefits = benefits.length > 0 ? benefits : benefitsMr;
      productData.benefitsMr = benefitsMr.length > 0 ? benefitsMr : benefits;
      productData.packagingOptions = packagingOptions.length > 0 ? packagingOptions : packagingMr;
      productData.packagingOptionsMr = packagingMr.length > 0 ? packagingMr : packagingOptions;
    }

    setIsSaving(true);
    Swal.fire({
      title: 'Saving Product Details...',
      text: 'Updating catalog databases',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      if (editingProduct?.id) {
        await setDoc(doc(db, 'products', editingProduct.id), productData);
      } else {
        await addDoc(collection(db, 'products'), productData);
      }

      setIsSaving(false);
      await Swal.fire({
        title: 'Success',
        text: 'Product listing saved successfully',
        icon: 'success',
        confirmButtonColor: '#2d6a4f',
      });
      setEditingProduct(null);
    } catch (error) {
      setIsSaving(false);
      console.error("Error saving product:", error);
      Swal.fire('Error', 'Failed to save product', 'error');
    }
  };

  // Delete Product from Firestore
  const handleDeleteProduct = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This product will be permanently removed from active product catalog listings!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete product'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'products', id));
        Swal.fire({
          title: 'Deleted!',
          text: 'Product catalog entry removed.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire('Error', 'Failed to delete product', 'error');
      }
    }
  };

  // Move product upward or downward
  const handleMoveProduct = async (product, direction) => {
    const list = [...filteredProducts];
    const currentIndex = list.findIndex(p => p.id === product.id);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= list.length) return;

    const targetProduct = list[targetIndex];

    // Assign numeric order to maintain custom position
    const currentOrder = product.order !== undefined ? Number(product.order) : currentIndex;
    const targetOrder = targetProduct.order !== undefined ? Number(targetProduct.order) : targetIndex;

    let newCurrentOrder = targetOrder;
    let newTargetOrder = currentOrder;

    if (newCurrentOrder === newTargetOrder) {
      newCurrentOrder = targetIndex;
      newTargetOrder = currentIndex;
    }

    try {
      await updateDoc(doc(db, 'products', product.id), { order: newCurrentOrder });
      await updateDoc(doc(db, 'products', targetProduct.id), { order: newTargetOrder });
    } catch (error) {
      console.error("Error moving product:", error);
      Swal.fire('Error', 'Failed to update product position', 'error');
    }
  };

  // Helper matching filter
  const isMatchCategory = (productCategory, filterCategory) => {
    if (filterCategory === 'all') return true;
    const pCat = (productCategory || '').toLowerCase();
    if (filterCategory === 'Food Products') {
      return pCat === 'food products' || pCat === 'sugar' || pCat === 'jaggery';
    }
    if (filterCategory === 'Industrial & Agricultural Products') {
      return pCat === 'industrial & agricultural products' || pCat === 'other';
    }
    return pCat === filterCategory.toLowerCase();
  };

  // Sort & Filter Products
  const sortedProducts = [...products].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const filteredProducts = sortedProducts.filter(product => isMatchCategory(product.category, categoryFilter));

  // Category counts
  const totalCount = products.length;
  const foodCount = products.filter(p => isMatchCategory(p.category, 'Food Products')).length;
  const industrialCount = products.filter(p => isMatchCategory(p.category, 'Industrial & Agricultural Products')).length;

  return (
    <div className="space-y-8 pb-20 font-poppins">
      
      {/* Header Banner */}
      <div className="bg-sugarcane-charcoal p-6 rounded-2xl text-white flex flex-col md:flex-row justify-between items-center gap-6 relative overflow-hidden shadow-md">
        <div className="absolute top-0 left-0 w-64 h-64 bg-sugarcane-green/10 rounded-full -ml-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-700/10 rounded-full -mr-32 -mb-32 blur-3xl"></div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-sugarcane-green rounded-xl flex items-center justify-center text-white">
              <Package size={20} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Product Catalogue</h2>
          </div>
          <p className="text-gray-400 text-sm italic">Manage Food Products and Industrial & Agricultural Products.</p>
        </div>

        <div className="relative z-10 flex gap-4">
          <button
            onClick={() => {
              setEditingProduct({});
              setFormCategory('Food Products');
            }}
            className="bg-sugarcane-green hover:bg-white hover:text-sugarcane-charcoal text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center space-x-2 transition-all shadow-md cursor-pointer"
          >
            <Plus size={18} />
            <span>New Product</span>
          </button>
        </div>
      </div>

      {/* Capsule Filter Switcher */}
      <div className="flex bg-gray-100/80 p-1 rounded-2xl max-w-2xl mx-auto border border-gray-200/40 shadow-inner backdrop-blur-md">
        {[
          { key: 'all', label: 'All Products', count: totalCount },
          { key: 'Food Products', label: 'Food Products', count: foodCount },
          { key: 'Industrial & Agricultural Products', label: 'Industrial & Agricultural', count: industrialCount }
        ].map((cat) => {
          const isActive = categoryFilter === cat.key;

          return (
            <button
              key={cat.key}
              onClick={() => setCategoryFilter(cat.key)}
              className={`flex-1 py-2.5 px-3 rounded-xl font-bold uppercase text-[10px] tracking-wider transition-all duration-300 flex items-center justify-center space-x-2 cursor-pointer ${
                isActive 
                  ? 'bg-sugarcane-green text-white shadow-md shadow-sugarcane-green/10' 
                  : 'text-gray-400 hover:text-sugarcane-charcoal'
              }`}
            >
              <span>{cat.label}</span>
              <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[8px] font-black tracking-normal ${
                isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-400'
              }`}>
                {cat.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div className="bg-white py-20 text-center max-w-sm mx-auto rounded-2xl shadow-sm border border-gray-100">
          <div className="bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100 shadow-inner">
            <HelpCircle className="text-gray-300" size={28} />
          </div>
          <h4 className="text-md font-bold text-sugarcane-charcoal mb-1">No Products Listed</h4>
          <p className="text-xs text-gray-400 leading-relaxed px-6">
            There are currently no products in this view. Click "New Product" above to create one.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AnimatePresence>
            {filteredProducts.map((product, pIdx) => {
              const isFood = (product.category || '').toLowerCase().includes('food') || 
                             (product.category || '').toLowerCase() === 'sugar' || 
                             (product.category || '').toLowerCase() === 'jaggery';

              const isFirst = pIdx === 0;
              const isLast = pIdx === filteredProducts.length - 1;

              return (
                <motion.div
                  key={product.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  whileHover={{ y: -4 }}
                  transition={{ type: 'tween', duration: 0.3 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between group overflow-hidden relative hover:-translate-y-1 hover:shadow-md hover:border-sugarcane-green/30 transition-all duration-300"
                >
                  <div>
                    <div className="flex flex-col lg:flex-row gap-5 items-start">
                      
                      {/* Image preview */}
                      <div className="w-full lg:w-32 h-32 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0 border border-gray-100 shadow-sm relative">
                        <img 
                          src={product.image || 'https://via.placeholder.com/150'} 
                          alt={product.name} 
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                        />
                      </div>

                      {/* Meta info & description */}
                      <div className="flex-grow">
                        <div className="flex justify-between items-center mb-2">
                          <span className={`px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider border ${
                            isFood 
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                            {isFood ? 'Food Products' : 'Industrial & Agricultural Products'}
                          </span>
                          
                          {/* Actions */}
                          <div className="flex space-x-1.5">
                            <button 
                              onClick={() => handleMoveProduct(product, 'up')}
                              disabled={isFirst}
                              className={`p-2 rounded-lg transition-all shadow-sm flex items-center justify-center border border-gray-100 cursor-pointer ${
                                isFirst
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                  : 'bg-emerald-50 text-emerald-600 hover:bg-sugarcane-green hover:text-white'
                              }`}
                              title="Move Upward"
                            >
                              <ArrowUp size={14} />
                            </button>
                            <button 
                              onClick={() => handleMoveProduct(product, 'down')}
                              disabled={isLast}
                              className={`p-2 rounded-lg transition-all shadow-sm flex items-center justify-center border border-gray-100 cursor-pointer ${
                                isLast
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                  : 'bg-emerald-50 text-emerald-600 hover:bg-sugarcane-green hover:text-white'
                              }`}
                              title="Move Downward"
                            >
                              <ArrowDown size={14} />
                            </button>
                            <button 
                              onClick={() => setEditingProduct(product)} 
                              className="p-2 bg-gray-50 text-blue-500 hover:bg-blue-500 hover:text-white rounded-lg transition-all shadow-sm flex items-center justify-center border border-gray-100 cursor-pointer"
                              title="Edit Product"
                            >
                              <Edit3 size={14} />
                            </button>
                            <button 
                              onClick={() => handleDeleteProduct(product.id)} 
                              className="p-2 bg-gray-50 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-all shadow-sm flex items-center justify-center border border-gray-100 cursor-pointer"
                              title="Delete Product"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        <h4 className="text-lg font-bold text-sugarcane-charcoal line-clamp-1 group-hover:text-sugarcane-green transition-colors mb-1.5">
                          {product.name}
                        </h4>
                        <p className="text-xs text-gray-500 leading-relaxed italic line-clamp-2 mb-3">
                          "{product.desc}"
                        </p>
                      </div>
                    </div>

                    {/* Packing Available for Food Products */}
                    {isFood && product.packingSizes && product.packingSizes.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-1.5">
                        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 flex items-center mr-1">
                          <Box size={10} className="mr-1" /> Packing:
                        </span>
                        {product.packingSizes.map((size, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[9px] font-extrabold rounded-md border border-emerald-100">
                            {size}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Key Benefits & Packaging for Industrial & Agricultural Products */}
                    {!isFood && (
                      <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
                        {product.benefits && product.benefits.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 flex items-center mr-1">
                              <CheckCircle2 size={10} className="mr-1" /> Benefits:
                            </span>
                            {product.benefits.slice(0, 3).map((benefit, bidx) => (
                              <span key={bidx} className="px-2 py-0.5 bg-amber-50 text-amber-800 text-[9px] font-bold rounded-md border border-amber-100">
                                {benefit}
                              </span>
                            ))}
                          </div>
                        )}

                        {product.packagingOptions && product.packagingOptions.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 flex items-center mr-1">
                              <Truck size={10} className="mr-1" /> Supply:
                            </span>
                            {product.packagingOptions.map((opt, oidx) => (
                              <span key={oidx} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-[9px] font-bold rounded-md border border-gray-200">
                                {opt}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Features list */}
                    {product.features && product.features.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {product.features.map((feature, fidx) => (
                          <span 
                            key={fidx} 
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-[8px] font-bold uppercase tracking-wider bg-gray-50 text-gray-500 border border-gray-200/50"
                          >
                            <Tag size={8} className="mr-1 text-gray-400" />
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Product Editor Modal */}
      <AnimatePresence>
        {editingProduct && (
          <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-sugarcane-charcoal/80 backdrop-blur-md admin-modal-overlay">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative border border-gray-100"
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white/95 backdrop-blur-md px-6 py-5 border-b border-gray-100 flex justify-between items-center z-10">
                <div>
                  <span className="px-3 py-1 bg-sugarcane-green/5 rounded-full text-[9px] font-bold uppercase tracking-widest text-sugarcane-green border border-sugarcane-green/10">
                    Product Registry
                  </span>
                  <h3 className="text-xl font-bold mt-1.5 tracking-tight text-sugarcane-charcoal">
                    {editingProduct.id ? 'Edit Product Entry' : 'Register New Product'}
                  </h3>
                </div>
                <button 
                  onClick={() => setEditingProduct(null)} 
                  className="p-2 bg-gray-50 hover:bg-gray-100 rounded-xl text-sugarcane-charcoal transition-colors cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form Details */}
              <form onSubmit={handleSaveProduct} className="p-6 space-y-6">

                {/* Form Language Toggle */}
                <div className="flex items-center justify-between bg-emerald/5 p-3.5 rounded-xl border border-emerald/10 mb-2">
                  <div className="flex items-center space-x-2 text-emerald">
                    <Globe size={16} />
                    <span className="text-xs font-bold uppercase tracking-wider text-sugarcane-charcoal">
                      Edit Language: <span className="text-emerald">{modalLang === 'en' ? 'English (🇬🇧)' : 'मराठी / Marathi (🇮🇳)'}</span>
                    </span>
                  </div>
                  <div className="flex bg-white p-1 rounded-lg border border-gray-200 shadow-sm gap-1">
                    <button
                      type="button"
                      onClick={() => setModalLang('en')}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${
                        modalLang === 'en' ? 'bg-sugarcane-green text-white shadow-sm' : 'text-gray-500 hover:text-sugarcane-charcoal'
                      }`}
                    >
                      English 🇬🇧
                    </button>
                    <button
                      type="button"
                      onClick={() => setModalLang('mr')}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${
                        modalLang === 'mr' ? 'bg-sugarcane-green text-white shadow-sm' : 'text-gray-500 hover:text-sugarcane-charcoal'
                      }`}
                    >
                      मराठी 🇮🇳
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                  
                  {/* Image Picker */}
                  <div className="md:col-span-1 bg-gray-50 border border-gray-200 rounded-2xl p-6 flex flex-col justify-center items-center shadow-sm">
                    <div className="w-full max-w-[240px] mx-auto space-y-4">
                      <div className="text-center mb-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400">
                          {formCategory === 'Food Products' ? 'Variant Image' : 'Product Image'}
                        </label>
                        <p className="text-[10px] text-gray-400 mt-1 font-medium">Recommended 1:1 format</p>
                      </div>
                      
                      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-white">
                        <ImageUpload onUploadSuccess={setUploadedImageUrl} initialImage={uploadedImageUrl} />
                      </div>
                    </div>
                  </div>

                  {/* Form Inputs */}
                  <div className="md:col-span-2 space-y-5">
                    
                    {/* Category Selector */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Category Class</label>
                        <select 
                          name="category" 
                          value={formCategory}
                          onChange={(e) => setFormCategory(e.target.value)}
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner"
                        >
                          <option value="Food Products">Food Products</option>
                          <option value="Industrial & Agricultural Products">Industrial & Agricultural Products</option>
                        </select>
                      </div>

                      {/* English Product Name */}
                      <div className={modalLang === 'en' ? 'block' : 'hidden'}>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                          {formCategory === 'Food Products' ? 'Product Variant Name (English)' : 'Product Name (English)'}
                        </label>
                        <input 
                          name="name" 
                          defaultValue={editingProduct.name} 
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" 
                          placeholder={formCategory === 'Food Products' ? 'e.g. M-30 White Sugar' : 'e.g. Bio-Compost Fertilizer'} 
                        />
                      </div>

                      {/* Marathi Product Name */}
                      <div className={modalLang === 'mr' ? 'block' : 'hidden'}>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                          {formCategory === 'Food Products' ? 'Product Variant Name (मराठी)' : 'Product Name (मराठी)'}
                        </label>
                        <input 
                          name="nameMr" 
                          defaultValue={editingProduct.nameMr} 
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" 
                          placeholder={formCategory === 'Food Products' ? 'उदा. एम-३० व्हाईट शुगर' : 'उदा. सेंद्रिय खत'} 
                        />
                      </div>
                    </div>

                    {/* Description / Overview (English) */}
                    <div className={modalLang === 'en' ? 'block' : 'hidden'}>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                        {formCategory === 'Food Products' ? 'Variant Description (English)' : 'Product Overview (English)'}
                      </label>
                      <textarea 
                        name="desc" 
                        defaultValue={editingProduct.desc} 
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner resize-none h-24" 
                        placeholder={formCategory === 'Food Products' ? 'Provide description representing variant quality...' : 'Provide detailed overview of industrial/agricultural product...'}
                      />
                    </div>

                    {/* Description / Overview (Marathi) */}
                    <div className={modalLang === 'mr' ? 'block' : 'hidden'}>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                        {formCategory === 'Food Products' ? 'Variant Description (मराठी)' : 'Product Overview (मराठी)'}
                      </label>
                      <textarea 
                        name="descMr" 
                        defaultValue={editingProduct.descMr} 
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner resize-none h-24" 
                        placeholder={formCategory === 'Food Products' ? 'गुणात्मक माहिती मराठीत द्या...' : 'उत्पादनाचे सविस्तर वर्णन मराठीत द्या...'}
                      />
                    </div>

                    {/* DYNAMIC FORM SECTION - FOOD PRODUCTS */}
                    {formCategory === 'Food Products' && (
                      <div className="space-y-5 pt-4 border-t border-gray-100">
                        {/* Food Features & Packing (English) */}
                        <div className={modalLang === 'en' ? 'space-y-5' : 'hidden'}>
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                              Key Selling Features (English, comma-separated)
                            </label>
                            <input 
                              name="features" 
                              defaultValue={editingProduct.features?.join(', ')} 
                              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" 
                              placeholder="e.g. 99.8% Polarized Purity, Sulphur-Free, Uniform Crystal Grain" 
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5">
                              <Box size={14} className="text-sugarcane-green" />
                              Packing Available (English, comma-separated sizes)
                            </label>
                            <input 
                              name="packingSizes" 
                              defaultValue={editingProduct.packingSizes?.join(', ')} 
                              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" 
                              placeholder="e.g. 500gm, 1 kg, 5 kg, 50 kg Bag" 
                            />
                          </div>
                        </div>

                        {/* Food Features & Packing (Marathi) */}
                        <div className={modalLang === 'mr' ? 'space-y-5' : 'hidden'}>
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                              Key Selling Features (मराठी, स्वल्पविराम देऊन)
                            </label>
                            <input 
                              name="featuresMr" 
                              defaultValue={editingProduct.featuresMr?.join(', ')} 
                              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" 
                              placeholder="उदा. ९९.८% शुद्धता, सल्फर-मुक्त, उत्तम दर्जा" 
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5">
                              <Box size={14} className="text-sugarcane-green" />
                              Packing Available (मराठी, स्वल्पविराम देऊन)
                            </label>
                            <input 
                              name="packingSizesMr" 
                              defaultValue={editingProduct.packingSizesMr?.join(', ')} 
                              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" 
                              placeholder="उदा. ५०० ग्रॅम, १ किलो, ५ किलो, ५० किलो बॅग" 
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* DYNAMIC FORM SECTION - INDUSTRIAL & AGRICULTURAL PRODUCTS */}
                    {formCategory === 'Industrial & Agricultural Products' && (
                      <div className="space-y-5 pt-4 border-t border-gray-100">
                        {/* Benefits & Packaging (English) */}
                        <div className={modalLang === 'en' ? 'space-y-5' : 'hidden'}>
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5">
                              <CheckCircle2 size={14} className="text-sugarcane-green" />
                              Key Benefits (English, comma-separated)
                            </label>
                            <textarea 
                              name="benefits" 
                              defaultValue={editingProduct.benefits?.join(', ') || editingProduct.features?.join(', ')} 
                              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner resize-none h-20" 
                              placeholder="e.g. Green Renewable Energy, Zero Carbon Footprint" 
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5">
                              <Truck size={14} className="text-sugarcane-green" />
                              Packaging & Supply Info (English, comma-separated)
                            </label>
                            <input 
                              name="packagingOptions" 
                              defaultValue={editingProduct.packagingOptions?.join(', ')} 
                              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" 
                              placeholder="e.g. Bulk Tanker, 10 kg, 200L HD Drums" 
                            />
                          </div>
                        </div>

                        {/* Benefits & Packaging (Marathi) */}
                        <div className={modalLang === 'mr' ? 'space-y-5' : 'hidden'}>
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5">
                              <CheckCircle2 size={14} className="text-sugarcane-green" />
                              Key Benefits (मराठी, स्वल्पविराम देऊन)
                            </label>
                            <textarea 
                              name="benefitsMr" 
                              defaultValue={editingProduct.benefitsMr?.join(', ') || editingProduct.featuresMr?.join(', ')} 
                              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner resize-none h-20" 
                              placeholder="उदा. पर्यावरणपूरक ऊर्जा, उच्च गुणवत्ता" 
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5">
                              <Truck size={14} className="text-sugarcane-green" />
                              Packaging & Supply Info (मराठी, स्वल्पविराम देऊन)
                            </label>
                            <input 
                              name="packagingOptionsMr" 
                              defaultValue={editingProduct.packagingOptionsMr?.join(', ')} 
                              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" 
                              placeholder="उदा. बल्क टँकर, १० किलो, २०० लि. ड्रम" 
                            />
                          </div>
                        </div>
                      </div>
                    )}

                  </div>

                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                  <button 
                    type="button" 
                    onClick={() => setEditingProduct(null)} 
                    className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-bold text-sm transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={isSaving}
                    className="px-6 py-2.5 bg-sugarcane-green hover:bg-sugarcane-charcoal text-white rounded-xl font-bold text-sm transition-all shadow-md shadow-sugarcane-green/10 flex items-center gap-2 cursor-pointer"
                  >
                    {isSaving ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <Plus size={16} />
                    )}
                    <span>{isSaving ? 'Saving Product...' : (editingProduct.id ? 'Save Changes' : 'Confirm Product')}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default AdminProducts;
